import type {
  Anchor,
  Frame,
  Point,
  RenderFn,
  RenderOutput,
} from "./pointField";
import {
  CUT_SCAT,
  EDGE_K,
  GLOW_K,
  SCAT,
  TWK,
  TWK_HZ,
  frontStates,
  lightAt,
} from "./tuning";

export interface OrbitResult {
  /** Title shown on the tag. */
  t: string;
  /** Archive reference. */
  r: string;
  /** Match 0–100. Drives orbit radius. */
  m: number;
}

export interface DetailFieldOptions {
  results: OrbitResult[];
  getNucleusRadius(): number;
  /** Per-frame readout values for the "rilievo del campo" panel. */
  onReadout(state: { lit: number; drift: number }): void;
}

export interface DetailField {
  render: RenderFn;
  /** Replay the entry burst — used when returning to the Esplora tab. */
  replay(): void;
  /** Collapse the particle field back to the nucleus — used on exit. */
  collapse(): void;
}

/** Golden angle in radians — guarantees even n distribution for any N. */
const GOLDEN_ANG = Math.PI * (3 - Math.sqrt(5));
const TAU = Math.PI * 2;
const HALF_PI = Math.PI / 2;
const DUST_N = 560;

type OrbitDesc = { Bj: number[]; aj: number };

/** Quadratic fade for front-facing orbit points that land over the nucleus.
 *  `zoneScale` controls how far from centre the fade begins (relative to NUC):
 *  - ring/satellite dots use 2.0 — tilted orbits can project near-centre from far out
 *  - main dot uses 1.4 — it sits at orbital radius, already well outside the nucleus
 *  Returns 1 when outside the fade zone or when hot (hovered). */
function nucFade(
  screenDist: number,
  isFront: boolean,
  hot: boolean,
  NUC: number,
  zoneScale: number,
): number {
  const fadeZone = NUC * zoneScale;
  if (hot || !isFront || screenDist >= fadeZone) return 1;
  return Math.max(0.04, (screenDist / fadeZone) ** 2);
}

/** Simple deterministic hash reused for layout jitter. */
function lhash(a: number, b: number): number {
  const n = Math.sin(a * 12.9898 + b * 78.233) * 43758.5453;
  return n - Math.floor(n);
}

export function createDetailField(opts: DetailFieldOptions): DetailField {
  const RES = opts.results;
  const N = RES.length;

  /* ------------------------------------------------------------------
     STATIC LABEL LAYOUT
     One flat ring. Angle: golden sequence. Radius: N-adaptive base
     with ±18% per-tag jitter for organic feel. The relaxation pass in
     ExploreTab handles actual screen-space separation.
     ------------------------------------------------------------------ */
  const LABEL = RES.map((_r, j) => ({
    ang: j * GOLDEN_ANG,
    jitter: 1 + (lhash(j, 3.7) * 2 - 1) * 0.18,
  }));

  /* Per-result orbital phase */
  const ORB = RES.map((_res, i) => {
    const f = 0.5 + 0.5 * Math.sin(i * 12.9898 + 8.9 * 78.233);
    const h = Math.abs(Math.sin(i * 7.31 + 2.1)) % 1;
    return {
      ph: h * TAU,
      sp: (0.05 + 0.03 * Math.abs(f)) * (f > 0.5 ? 1 : -1),
    };
  });

  let lastT: number | null = null;
  let foc = 0; // 0 = browsing, 1 = focused on one

  /* ------------------------------------------------------------------
     ENTRY BURST
     ------------------------------------------------------------------ */
  const ENT = {
    delay: 0.35,
    dur: 2.3,
    stag: 0.3,
    a: 3.2,
    b: 5.6,
    t0: 0,
    done: false,
    replay: false,
  };

  const COL = {
    active: false,
    dur: 1.4,
    t0: 0,
  };

  function spring(u: number): number {
    if (u <= 0) return 0;
    if (u >= 1) return 1;
    return 1 - (1 - u) * Math.exp(-ENT.a * u) * Math.cos(ENT.b * u);
  }

  type ProjFn = (x: number, y: number, z: number) => [number, number, number];
  type JitFn = (seed: number) => number;
  type TwFn = (seed: number) => number;

  interface FrameCtx {
    g: Frame;
    p: Frame["pal"];
    REST: number;
    proj: ProjFn;
    jitOf: JitFn;
    twOf: TwFn;
    F: ReturnType<typeof frontStates>;
  }

  function burst(g: Frame, seed: number): number {
    if (COL.active) {
      const C = Math.min(1, (g.t - COL.t0) / COL.dur);
      return Math.max(0, spring(1 - C - ENT.stag * g.hash(seed, 11.3)));
    }
    if (ENT.done) return 1;
    const E = (g.t - ENT.t0 - ENT.delay) / ENT.dur;
    if (E >= 1 + ENT.stag) {
      ENT.done = true;
      return 1;
    }
    return spring(E - ENT.stag * g.hash(seed, 11.3));
  }

  function renderDust(ctx: FrameCtx, pts: Point[], NUC: number): number {
    const { g, p, REST, proj, jitOf, twOf, F } = ctx;
    let lit = 0;
    for (let i = 0; i < DUST_N; i++) {
      const v3 = g.fib(i, DUST_N);
      const rad =
        (NUC * 1.06 + (g.R * 1.06 - NUC * 1.06) * g.hash(i, 3.1)) * burst(g, i);
      const nxu = (v3[0] * rad) / g.R;
      const nyu = (v3[1] * rad) / g.R;
      const nzu = (v3[2] * rad) / g.R;
      const Q = proj(v3[0] * rad, v3[1] * rad, v3[2] * rad);
      const dep = (Q[2] / rad + 1) / 2;
      const L = lightAt(F, nxu, nyu, nzu, jitOf(i));
      const gl = L[0];
      const ed = L[1] * twOf(i);
      if (L[1] > 0.35) lit++;
      pts.push({
        x: Q[0],
        y: Q[1],
        z: Q[2],
        r: (0.42 + 0.8 * dep + 0.85 * EDGE_K * ed + 0.22 * GLOW_K * gl) * g.rs,
        c: p.dust,
        a: (0.05 + 0.09 * dep + 0.2 * GLOW_K * gl + 0.26 * EDGE_K * ed) * REST,
        blur: 0,
      });
    }
    return lit;
  }

  function renderAmbientOrbits(ctx: FrameCtx, pts: Point[]): void {
    const { g, p, REST, proj, jitOf, twOf, F } = ctx;
    for (let o = 0; o < 11; o++) {
      const w = g.hash(o, 1.7);
      const T = g.hash(o, 5.2);
      const f2 = g.hash(o, 8.9);
      const a = g.R * (0.45 + 0.52 * w) * burst(g, 200 + o);
      const phi = w * TAU;
      const the = Math.acos(2 * T - 1);
      const mx = Math.sin(the) * Math.cos(phi);
      const my = Math.cos(the);
      const mz = Math.sin(the) * Math.sin(phi);
      const B = g.basis(mx, my, mz);
      const sp = (0.1 + 0.2 * f2) * (f2 > 0.5 ? 1 : -1);

      for (let q = 0; q < 40; q++) {
        const A = (q / 40) * TAU;
        const C = Math.cos(A);
        const S = Math.sin(A);
        const ox = (B[0] * C + B[3] * S) * a;
        const oy = (B[1] * C + B[4] * S) * a;
        const oz = (B[2] * C + B[5] * S) * a;
        const Qo = proj(ox, oy, oz);
        const dpo = (Qo[2] / a + 1) / 2;
        const so = o * 41 + q;
        const Lo = lightAt(F, ox / g.R, oy / g.R, oz / g.R, jitOf(so));
        const eo = Lo[1] * twOf(so);
        pts.push({
          x: Qo[0],
          y: Qo[1],
          z: Qo[2],
          r: (0.62 + 0.7 * EDGE_K * eo + 0.18 * GLOW_K * Lo[0]) * g.rs,
          c: p.mid,
          a:
            (0.055 + 0.11 * dpo + 0.17 * GLOW_K * Lo[0] + 0.22 * EDGE_K * eo) *
            REST,
        });
      }

      for (let k2 = 0; k2 < 2; k2++) {
        const A2 = g.t * sp + k2 * Math.PI + T * 6;
        const C2 = Math.cos(A2);
        const S2 = Math.sin(A2);
        const px2 = (B[0] * C2 + B[3] * S2) * a;
        const py2 = (B[1] * C2 + B[4] * S2) * a;
        const pz2 = (B[2] * C2 + B[5] * S2) * a;
        const Q2 = proj(px2, py2, pz2);
        const dp2 = (Q2[2] / a + 1) / 2;
        const L2 = lightAt(
          F,
          px2 / g.R,
          py2 / g.R,
          pz2 / g.R,
          jitOf(o * 41 + 300 + k2),
        );
        pts.push({
          x: Q2[0],
          y: Q2[1],
          z: Q2[2],
          r: (0.58 + 0.78 * dp2 + 0.36 * EDGE_K * L2[1]) * g.rs,
          c: p.ink,
          a: (0.13 + 0.21 * dp2 + 0.09 * GLOW_K * L2[0]) * REST,
        });
      }
    }
  }

  function renderCutRings(ctx: FrameCtx, pts: Point[]): void {
    const { g, p, REST, proj, twOf, F } = ctx;
    for (let k3 = 0; k3 < F.length; k3++) {
      const eb = burst(g, 300 + k3);
      const fr3 = F[k3];
      const cr = Math.sqrt(Math.max(0, 1 - fr3.p * fr3.p)) * g.R * eb;
      if (cr < 6) continue;
      const Bc = g.basis(fr3.n[0], fr3.n[1], fr3.n[2]);
      const ctr = [
        fr3.n[0] * fr3.p * g.R * eb,
        fr3.n[1] * fr3.p * g.R * eb,
        fr3.n[2] * fr3.p * g.R * eb,
      ];
      for (let s3 = 0; s3 < 130; s3++) {
        const A3 = (s3 / 130) * TAU;
        const C3 = Math.cos(A3);
        const S3 = Math.sin(A3);
        const s3s = s3 + k3 * 131;
        const rr = cr * (1 + (g.hash(s3s, 2.7) * 2 - 1) * CUT_SCAT * 2.2);
        const nof = (g.hash(s3s, 7.9) * 2 - 1) * CUT_SCAT * g.R;
        const Qc = proj(
          ctr[0] + (Bc[0] * C3 + Bc[3] * S3) * rr + fr3.n[0] * nof,
          ctr[1] + (Bc[1] * C3 + Bc[4] * S3) * rr + fr3.n[1] * nof,
          ctr[2] + (Bc[2] * C3 + Bc[5] * S3) * rr + fr3.n[2] * nof,
        );
        pts.push({
          x: Qc[0],
          y: Qc[1],
          z: Qc[2] + 1,
          r: 0.62 * g.rs,
          c: p.mid,
          a: 0.085 * twOf(s3s) * REST,
          blur: 0,
        });
      }
    }
  }

  function renderOrbitRing(
    ctx: FrameCtx,
    pts: Point[],
    orb: OrbitDesc,
    j: number,
    hot: boolean,
    mine: number,
    NUC: number,
  ): void {
    const { Bj, aj } = orb;
    const { g, p, proj, jitOf, twOf, F } = ctx;
    for (let gg = 0; gg < 76; gg++) {
      const Yg = (gg / 76) * TAU;
      const gx = (Bj[0] * Math.cos(Yg) + Bj[3] * Math.sin(Yg)) * aj;
      const gy = (Bj[1] * Math.cos(Yg) + Bj[4] * Math.sin(Yg)) * aj;
      const gz = (Bj[2] * Math.cos(Yg) + Bj[5] * Math.sin(Yg)) * aj;
      const Qg = proj(gx, gy, gz);
      const dg = (Qg[2] / aj + 1) / 2;
      const sg = 1000 + j * 97 + gg;
      const Lg = lightAt(F, gx / g.R, gy / g.R, gz / g.R, jitOf(sg));
      const eg = Lg[1] * twOf(sg);
      const isFront = Qg[2] > 0;
      const screenDist = Math.hypot(Qg[0] - g.cx, Qg[1] - g.cy);
      pts.push({
        x: Qg[0],
        y: Qg[1],
        z: Qg[2],
        r: ((hot ? 1.25 : 0.9) + 0.9 * EDGE_K * eg) * g.rs,
        c: p.gold,
        a:
          ((hot ? 0.72 : 0.3) * (0.4 + 0.6 * dg) + 0.2 * EDGE_K * eg) *
          mine *
          nucFade(screenDist, isFront, hot, NUC, 2.0),
      });
    }
  }

  function renderSatellites(
    ctx: FrameCtx,
    pts: Point[],
    orb: OrbitDesc,
    A0: number,
    hot: boolean,
    mine: number,
    NUC: number,
  ): void {
    const { Bj, aj } = orb;
    const { g, p, proj } = ctx;
    for (let pq = 0; pq < 3; pq++) {
      const Ap = A0 + (pq + 1) * HALF_PI;
      const sx = (Bj[0] * Math.cos(Ap) + Bj[3] * Math.sin(Ap)) * aj;
      const sy2 = (Bj[1] * Math.cos(Ap) + Bj[4] * Math.sin(Ap)) * aj;
      const sz = (Bj[2] * Math.cos(Ap) + Bj[5] * Math.sin(Ap)) * aj;
      const Qs = proj(sx, sy2, sz);
      const ds = (Qs[2] / aj + 1) / 2;
      const isFront = Qs[2] > 0;
      const screenDist = Math.hypot(Qs[0] - g.cx, Qs[1] - g.cy);
      pts.push({
        x: Qs[0],
        y: Qs[1],
        z: Qs[2],
        r: ((hot ? 1.5 : 1.1) + 1.3 * ds) * g.rs,
        c: p.gold,
        a:
          (0.4 + 0.35 * ds) *
          mine *
          nucFade(screenDist, isFront, hot, NUC, 2.0),
      });
    }
  }

  function renderHotLeader(
    ctx: FrameCtx,
    pts: Point[],
    Qb: [number, number, number],
  ): void {
    const { g, p } = ctx;
    for (let k4 = 2; k4 < 17; k4++) {
      const f4 = k4 / 17;
      pts.push({
        x: g.cx + (Qb[0] - g.cx) * f4,
        y: g.cy + (Qb[1] - g.cy) * f4,
        z: Qb[2] * f4,
        r: 0.7 * g.rs,
        c: p.gold,
        a: 0.34,
      });
    }
    for (let h4 = 0; h4 < 52; h4++) {
      const B4 = (h4 / 52) * TAU;
      const RR = 15 * g.rs;
      pts.push({
        x: Qb[0] + Math.cos(B4) * RR,
        y: Qb[1] + Math.sin(B4) * RR,
        z: Qb[2] + 3,
        r: 0.85 * g.rs,
        c: p.gold,
        a: 0.45,
      });
    }
  }

  function renderLabelLeader(
    ctx: FrameCtx,
    pts: Point[],
    sats: Anchor[],
    Qb: [number, number, number],
    anchor: [number, number],
    hot: boolean,
    mine: number,
  ): void {
    const { g, p, REST } = ctx;
    const [ax, ay] = anchor;
    const LN = hot ? 16 : 10;
    for (let k5 = 0; k5 < LN; k5++) {
      const f5 = 0.2 + 0.7 * (k5 / (LN - 1));
      pts.push({
        x: ax + (Qb[0] - ax) * f5,
        y: ay + (Qb[1] - ay) * f5,
        z: Qb[2] * f5,
        r: (hot ? 0.85 : 0.6) * g.rs,
        c: p.gold,
        a: (hot ? 0.4 : 0.15) * mine,
      });
    }
    sats.push([ax, ay, 1, (hot ? 1 : 0.34 + 0.52 * REST).toFixed(2)]);
  }

  function renderOneResult(
    ctx: FrameCtx,
    pts: Point[],
    sats: Anchor[],
    j: number,
    NUC: number,
  ): void {
    const { g, p, REST, proj, jitOf, F } = ctx;
    const res = RES[j];
    const hot = g.hover === j;
    const lbl = LABEL[j];

    // Base ring radius scales with N: expands as tag count grows.
    const baseR =
      NUC * 2.0 +
      (g.R * 0.78 - NUC * 2.0) * Math.min(1, Math.max(0, (N - 3) / 10));
    const ajRest = Math.max(
      NUC * 1.34,
      Math.min(g.R * 0.92, baseR * lbl.jitter),
    );
    const aj = ajRest * burst(g, 400 + j);

    const wj = g.hash(j + 40, 1.7);
    const Tj = g.hash(j + 40, 5.2);
    const thj = Math.acos(2 * Tj - 1);
    const jx = Math.sin(thj) * Math.cos(wj * TAU);
    const jy = Math.cos(thj);
    const jz = Math.sin(thj) * Math.sin(wj * TAU);
    const Bj = g.basis(jx, jy, jz);
    const mine = hot ? 1 : REST;

    const A0 = ORB[j].ph;
    const bx = (Bj[0] * Math.cos(A0) + Bj[3] * Math.sin(A0)) * aj;
    const by = (Bj[1] * Math.cos(A0) + Bj[4] * Math.sin(A0)) * aj;
    const bz = (Bj[2] * Math.cos(A0) + Bj[5] * Math.sin(A0)) * aj;
    const Qb = proj(bx, by, bz);
    const Lb = lightAt(F, bx / g.R, by / g.R, bz / g.R, jitOf(2000 + j));

    const orb: OrbitDesc = { Bj, aj };
    renderOrbitRing(ctx, pts, orb, j, hot, mine, NUC);
    renderSatellites(ctx, pts, orb, A0, hot, mine, NUC);
    if (hot) renderHotLeader(ctx, pts, Qb);

    const mainDist = Math.hypot(Qb[0] - g.cx, Qb[1] - g.cy);
    const mainFade = nucFade(mainDist, Qb[2] + 4 > 0, hot, NUC, 1.4);
    pts.push({
      x: Qb[0],
      y: Qb[1],
      z: Qb[2] + 4,
      r:
        (3.0 + (2.3 * (res.m - 60)) / 40 + 1.6 * EDGE_K * Lb[1]) *
        g.rs *
        (hot ? 1.45 : 1),
      c: p.gold,
      a: Math.min(1, hot ? 1.0 : 0.92) * burst(g, 400 + j) * mainFade,
      blur: (hot ? 18 : 9) + 8 * EDGE_K * Lb[1],
    });

    const ar = Math.max(NUC * 2.0, ajRest);
    const ax = g.cx + ar * Math.cos(lbl.ang);
    const ay = g.cy + ar * Math.sin(lbl.ang);
    if (ENT.done) renderLabelLeader(ctx, pts, sats, Qb, [ax, ay], hot, mine);
    else sats.push([ax, ay, 1, (hot ? 1 : 0.34 + 0.52 * REST).toFixed(2)]);
  }

  function renderResultOrbits(
    ctx: FrameCtx,
    pts: Point[],
    sats: Anchor[],
    NUC: number,
  ): void {
    for (let j = 0; j < RES.length; j++)
      renderOneResult(ctx, pts, sats, j, NUC);
  }

  const render: RenderFn = (g: Frame): RenderOutput => {
    const p = g.pal;
    const pts: Point[] = [];
    const sats: Anchor[] = [];
    const NUC = opts.getNucleusRadius();

    if (ENT.replay) {
      ENT.replay = false;
      ENT.done = false;
      ENT.t0 = g.t;
    }

    const dt = lastT === null ? 0 : Math.max(0, Math.min(0.08, g.t - lastT));
    lastT = g.t;
    foc += ((g.hover >= 0 ? 1 : 0) - foc) * Math.min(1, dt * 5.5);
    for (let oi = 0; oi < ORB.length; oi++) {
      if (g.hover !== oi) ORB[oi].ph += dt * ORB[oi].sp;
    }

    const REST = 1 - 0.58 * foc;
    const proj = g.projector(g.t * 0.041, 0.32, g.cx, g.cy, 1);
    const jitOf = (seed: number): number => (g.hash(seed, 9.7) * 2 - 1) * SCAT;
    const twOf = (seed: number): number =>
      1 -
      TWK *
        (0.5 +
          0.5 *
            Math.sin(
              g.t * TWK_HZ * (0.6 + 1.1 * g.hash(seed, 4.3)) +
                g.hash(seed, 6.1) * TAU,
            ));
    const F = frontStates(g.t);
    const ctx: FrameCtx = { g, p, REST, proj, jitOf, twOf, F };
    const lit = renderDust(ctx, pts, NUC);
    renderAmbientOrbits(ctx, pts);
    renderCutRings(ctx, pts);
    renderResultOrbits(ctx, pts, sats, NUC);

    opts.onReadout({
      lit: Math.round(12847 * (lit / DUST_N)),
      drift: 0.42 + 0.31 * Math.sin(g.t * 0.11),
    });

    return { pts, sats };
  };

  return {
    render,
    replay(): void {
      ENT.replay = true;
    },
    collapse(): void {
      COL.active = true;
      COL.t0 = lastT ?? 0;
    },
  };
}
